import { RcFile, UploadFile } from 'antd/es/upload/interface';
import { options } from '../config/config';
import { message, Upload } from 'antd';
import { IntlShape } from 'react-intl';

export const beforeUpload = (file: RcFile, intl: IntlShape) => {
  const maxSizeInMB = options.images.maxSizeInMB;
  const allowedFormats = options.images.allowedFormats;

  if (file.size / 1024 / 1024 > maxSizeInMB) {
    message.error(
      intl.formatMessage({
        id: 'validation.max_file_size',
      })
    );
    return Upload.LIST_IGNORE;
  }

  if (!allowedFormats.includes(file.type)) {
    message.error(
      intl.formatMessage({
        id: 'validation.allowed_formats',
      })
    );
    return Upload.LIST_IGNORE;
  }
  return true;
};

export const getMimeType = (fileName: any) => {
  const extension = fileName.split('.').pop().toLowerCase();

  const mimeTypes: any = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
  };
  return mimeTypes[extension] || 'application/octet-stream';
};

export const normalizeFileNames = (files: UploadFile[]): UploadFile[] => {
  const nameCounts: Record<string, number> = {};
  const usedNames = new Set<string>();

  return files.map((file) => {
    const extMatch = file.name.match(/\.[^/.]+$/);
    const ext = extMatch ? extMatch[0] : '';

    const actualName = file.name.replace(/^([^_]+_\d+_)/, '');
    const baseName = actualName.replace(ext, '');

    let newBase = baseName;
    let suffix = nameCounts[baseName] ?? 0;
    let newName = `${newBase}${ext}`;

    while (usedNames.has(newName)) {
      suffix += 1;
      newName = `${newBase}_${suffix}${ext}`;
    }

    nameCounts[baseName] = suffix;
    usedNames.add(newName);

    if (!file.originFileObj) {
      return {
        ...file,
        name: newName,
      };
    }

    const newFile = new File([file.originFileObj], newName, {
      type: file.originFileObj?.type,
    }) as RcFile;

    return {
      ...file,
      name: newName,
      originFileObj: newFile,
    };
  });
};

export const TRANSPARENT_IMAGE_TYPES = ['image/png', 'image/webp', 'image/gif'];

const FLATTEN_JPEG_QUALITY = 0.92;

export const flattenToJpeg = (file: RcFile): Promise<RcFile> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    const done = (result: RcFile) => {
      URL.revokeObjectURL(url);
      resolve(result);
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        done(file);
        return;
      }

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            done(file);
            return;
          }

          const name = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
          const jpeg = new File([blob], name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          }) as RcFile;
          jpeg.uid = file.uid;
          done(jpeg);
        },
        'image/jpeg',
        FLATTEN_JPEG_QUALITY
      );
    };

    img.onerror = () => done(file);
    img.src = url;
  });
